/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useMemo } from 'react';

import { jsx } from '@compiled/react';
import { RouteComponent, Router, createBrowserHistory } from 'react-resource-router';

import { AppProvider } from '@atlaskit/app-provider/app-provider';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { Root, Main } from '@atlaskit/navigation-system';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { initAnalyticsClient, type LoggedInUser } from '@atlassian/twg-microsite-experience';
import { AnalyticsProvider } from '@atlassian/twg-microsite-experience/analytics-provider';
import { DebugPanel } from '@atlassian/twg-microsite-experience/DebugPanel';
import { LocaleProvider } from '@atlassian/twg-microsite-experience/locale-provider';
import { NavigationBar } from '@atlassian/twg-microsite-experience/NavigationBar';
import { PageErrorView } from '@atlassian/twg-microsite-experience/PageErrorView';
import { PanelProvider } from '@atlassian/twg-microsite-experience/panel-provider';
import { SentryErrorBoundary } from '@atlassian/twg-microsite-experience/sentry/error-boundary';
import { useResetScrollOnRouteChange } from '@atlassian/twg-microsite-experience/use-reset-scroll-on-route-change';
import { UserProvider } from '@atlassian/twg-microsite-experience/user-provider';

import { useReactUFORouteWatcher } from '../analytics/react-ufo/useReactUFORouteWatcher';
import { initializeFeatureGates } from '../feature-flags';
import { routes } from './routes';
import { useDocumentTitleOnRouteChange } from './routes/document-title';
import { resolveInitialLocation } from './routes/initial-location';
// Normalise the entry URL before the Router subscribes: strip a trailing slash so
// `/cli/` reaches the CLI route rather than being treated as unknown, and rewrite
// anything genuinely unknown to `/`. See `./routes/initial-location`.

const history = createBrowserHistory();
const initialLocation = resolveInitialLocation(history.location);
if (initialLocation.pathname !== history.location.pathname) {
	history.replace(initialLocation);
}

// Sibling of `<Root>` so it can subscribe to the `Router` context. The hook
// is a no-op on first render (the initial page-load is traced by `initUFO`)
// and fires `traceUFOTransition` on subsequent route name changes.
const RouteWatcher = (): null => {
	useReactUFORouteWatcher();
	return null;
};

// Also a sibling of `<Root>` so it can subscribe to the `Router` context.
// Returns the viewport to the top when routing between `/` and `/cli`, except
// for deep links, whose section scroll the destination page owns.
const ScrollReset = (): null => {
	useResetScrollOnRouteChange();
	return null;
};

// Also a sibling of `<Root>`. Each route ships its own HTML document, so a full
// page load arrives with the right `<title>`; an SPA navigation does not fetch the
// destination document, so the tab title needs updating in the client.
const DocumentTitle = (): null => {
	useDocumentTitleOnRouteChange();
	return null;
};

export const App = (): JSX.Element => {
	const analyticsClient = useMemo(() => initAnalyticsClient(), []);

	// Re-identify the Statsig feature-gate client with the logged-in
	// user's identifiers. The anonymous Statsig bootstrap that runs in
	// `index.tsx` has already installed the `fg(...)` resolver, so this
	// call upgrades the existing client via `FeatureGates.updateUser`
	// rather than initializing from scratch — see `../feature-flags`.
	const onUserLoginComplete = useCallback(async (user: LoggedInUser) => {
		await initializeFeatureGates(user.accountId, user.orgId);
	}, []);

	return (
		<UFOSegment name="twg-microsite-root">
			<RelayEnvironmentProvider>
				<AnalyticsProvider analyticsClient={analyticsClient}>
					<AppProvider defaultColorMode="dark">
						<FlagsProvider>
							{/* Any component or provider that requires a FG to be initialized must be wrapped in UserProvider.
							    This also means there should be NO FGs being resolved outside of UserProvider */}
							<UserProvider onUserLoginComplete={onUserLoginComplete}>
								<LocaleProvider>
									<PanelProvider>
										<Router history={history} routes={routes}>
											<RouteWatcher />
											<ScrollReset />
											<DocumentTitle />
											<Root>
												<Main>
													{/* The nav bar renders outside the route, so the page-level
													    boundaries inside `Home` and `CliPage` cannot catch a crash in
													    it — an uncaught one unmounts the whole app and leaves a bare
													    black page. Wrapping the nav bar and the route together turns
													    that into the same full-page error the pages show. */}
													<SentryErrorBoundary
														fallback={<PageErrorView />}
														loggerName="micrositeShell.errorBoundary"
													>
														<NavigationBar />
														<RouteComponent />
													</SentryErrorBoundary>
												</Main>
												<DebugPanel />
											</Root>
										</Router>
									</PanelProvider>
								</LocaleProvider>
							</UserProvider>
						</FlagsProvider>
					</AppProvider>
				</AnalyticsProvider>
			</RelayEnvironmentProvider>
		</UFOSegment>
	);
};
